import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API_URL_SERVER } from '../../api/api';
import { AuthContext } from '../../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { toast } from 'react-toastify';

const Users = () => {
    const { user } = useContext(AuthContext);
    const [users, setUsers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        accountIds: [],
        trades: false,
        show_balance: false,
        is_enabled: true,
    });
    const [editData, setEditData] = useState({
        username: '',
        password: '',
        accountIds: [],
        trades: false,
        show_balance: false,
        is_enabled: true,
        originalUsername: '',
    });
    const [showForm, setShowForm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showTimestampsModal, setShowTimestampsModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        if (user?.role === 'admin') {
            fetchUsers();
            fetchAccounts();
        }
    }, [user]);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_URL_SERVER}/users`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            const usersData = Array.isArray(response.data.data) ? response.data.data : [];
            setUsers(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch users');
            setUsers([]);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(`${API_URL_SERVER}/accounts`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setAccounts(response.data);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to fetch accounts');
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox' && name.startsWith('accountId_')) {
            const accountId = name.split('_')[1];
            setFormData((prev) => ({
                ...prev,
                accountIds: checked
                    ? [...prev.accountIds, accountId]
                    : prev.accountIds.filter((id) => id !== accountId),
            }));
        } else if (type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox' && name.startsWith('accountId_')) {
            const accountId = name.split('_')[1];
            setEditData((prev) => ({
                ...prev,
                accountIds: checked
                    ? [...prev.accountIds, accountId]
                    : prev.accountIds.filter((id) => id !== accountId),
            }));
        } else if (type === 'checkbox') {
            setEditData((prev) => ({ ...prev, [name]: checked }));
        } else {
            setEditData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API_URL_SERVER}/register`, formData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            toast.success(response.data.message);
            setFormData({ username: '', password: '', accountIds: [], trades: false, show_balance: false, is_enabled: true });
            fetchUsers();
            setShowForm(false);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to register user');
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.put(
                `${API_URL_SERVER}/users/${editData.originalUsername}`,
                {
                    username: editData.username,
                    password: editData.password || undefined,
                    accountIds: editData.accountIds,
                    trades: editData.trades,
                    show_balance: editData.show_balance,
                    is_enabled: editData.is_enabled,
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                }
            );
            toast.success(response.data.message);
            setShowEditModal(false);
            setEditData({ username: '', password: '', accountIds: [], trades: false, show_balance: false, is_enabled: true, originalUsername: '' });
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update user');
        }
    };

    const openEditModal = (user) => {
        setEditData({
            username: user.username,
            password: '',
            accountIds: user.accountIds || [],
            trades: user.trades || false,
            show_balance: user.show_balance || false,
            is_enabled: user.is_enabled !== false,
            originalUsername: user.username,
        });
        setShowEditModal(true);
    };

    const openDeleteModal = (username) => {
        setUserToDelete(username);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        try {
            const response = await axios.delete(`${API_URL_SERVER}/users/${userToDelete}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            toast.success(response.data.message);
            setShowDeleteModal(false);
            setUserToDelete(null);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete user');
        }
    };

    const toggleUserStatus = async (username, currentStatus) => {
        try {
            const response = await axios.put(
                `${API_URL_SERVER}/users/${username}`,
                { is_enabled: !currentStatus },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                }
            );
            toast.success(response.data.message);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to toggle user status');
        }
    };

    const openTimestampsModal = (user) => {
        setSelectedUser(user);
        setShowTimestampsModal(true);
    };

    if (user?.role !== 'admin') {
        return <div className="container mt-5"><h2>Unauthorized</h2></div>;
    }

    return (
        <div className="mt-5">
            <div className='me-3'>
                <div className="justify-content-between align-items-center mb-4">
                    <h1 className="text-center">All Users</h1>
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
                            {showForm ? 'Close Form' : 'Add User'}
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div className="row justify-content-center mb-5">
                        <div className="col-md-6">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label htmlFor="username" className="form-label">Username</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="username"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="password" className="form-label">Password</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Accounts</label>
                                    <div className="d-flex flex-column">
                                        {accounts.map((acc) => (
                                            <div key={acc.mt5AccountId} className="form-check me-3 mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`accountId_${acc.mt5AccountId}`}
                                                    name={`accountId_${acc.mt5AccountId}`}
                                                    checked={formData.accountIds.includes(acc.mt5AccountId)}
                                                    onChange={handleChange}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor={`accountId_${acc.mt5AccountId}`}
                                                >
                                                    {acc.nickName} ({acc.mt5AccountId})
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-3 form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="trades"
                                        name="trades"
                                        checked={formData.trades}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label" htmlFor="trades">Enable Trades</label>
                                </div>
                                <div className="mb-3 form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="show_balance"
                                        name="show_balance"
                                        checked={formData.show_balance}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label" htmlFor="show_balance">Show Balance</label>
                                </div>
                                <div className="mb-3 form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_enabled"
                                        name="is_enabled"
                                        checked={formData.is_enabled}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label" htmlFor="is_enabled">Enable User</label>
                                </div>
                                <button type="submit" className="btn btn-primary">Register User</button>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            <div className="table-responsive">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>No.</th>
                            <th>Username</th>
                            <th>Role</th>
                            <th>Accounts</th>
                            <th>Trades</th>
                            <th>Show Balance</th>
                            <th>Status</th>
                            <th>Visit Count</th> {/* New column */}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length > 0 ? (
                            users.map((u, index) => (
                                <tr key={u.username}>
                                    <td>{index + 1}</td>
                                    <td>{u.username}</td>
                                    <td>{u.role}</td>
                                    <td>
                                        {Array.isArray(u.accountIds) && u.accountIds.length > 0
                                            ? u.accountIds.map(id => {
                                                const acc = accounts.find(a => a.mt5AccountId === id);
                                                return acc ? `${acc.nickName} (${id})` : id;
                                            }).join(', ')
                                            : u.accountId
                                                ? (() => {
                                                    const acc = accounts.find(a => a.mt5AccountId === u.accountId);
                                                    return acc ? `${acc.nickName} (${u.accountId})` : u.accountId;
                                                })()
                                                : 'N/A'}
                                    </td>
                                    <td>{u.trades ? 'Enabled' : 'Disabled'}</td>
                                    <td>{u.show_balance ? 'Enabled' : 'Disabled'}</td>
                                    <td>{u.is_enabled !== false ? 'Enabled' : 'Disabled'}</td>
                                    <td 
                                        onClick={() => openTimestampsModal(u)} 
                                        style={{cursor: 'pointer', textDecoration: 'underline', color: 'blue'}}
                                    >
                                        {u.visit_count || 0}
                                    </td>
                                    <td className='d-flex'>
                                        <button
                                            className="btn btn-sm btn-warning me-2"
                                            onClick={() => openEditModal(u)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className={`btn btn-sm ${u.is_enabled !== false ? 'btn-secondary' : 'btn-success'} me-2`}
                                            onClick={() => toggleUserStatus(u.username, u.is_enabled !== false)}
                                        >
                                            {u.is_enabled !== false ? 'Disable' : 'Enable'}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => openDeleteModal(u.username)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="text-center">No users found</td> {/* Update colSpan */}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit User Modal */}
            {showEditModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit User</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowEditModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleEditSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="editUsername" className="form-label">Username</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editUsernameLabel"
                                            name="username"
                                            value={editData.username}
                                            onChange={handleEditChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editPassword" className="form-label">New Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="editPassword"
                                            name="password"
                                            value={editData.password}
                                            onChange={handleEditChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Accounts</label>
                                        <div className="d-flex flex-wrap">
                                            {accounts.map((acc) => (
                                                <div key={acc.mt5AccountId} className="form-check me-3 mb-2">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={`editAccountId_${acc.mt5AccountId}`}
                                                        name={`accountId_${acc.mt5AccountId}`}
                                                        checked={editData.accountIds.includes(acc.mt5AccountId)}
                                                        onChange={handleEditChange}
                                                    />
                                                    <label
                                                        className="form-check-label"
                                                        htmlFor={`editAccountId_${acc.mt5AccountId}`}
                                                    >
                                                        {acc.nickName} ({acc.mt5AccountId})
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="editTrades"
                                            name="trades"
                                            checked={editData.trades}
                                            onChange={handleEditChange}
                                        />
                                        <label className="form-check-label" htmlFor="editTrades">Enable Trades</label>
                                    </div>
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="editShowBalance"
                                            name="show_balance"
                                            checked={editData.show_balance}
                                            onChange={handleEditChange}
                                        />
                                        <label className="form-check-label" htmlFor="editShowBalance">Show Balance</label>
                                    </div>
                                    <div className="mb-3 form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="editIsEnabled"
                                            name="is_enabled"
                                            checked={editData.is_enabled}
                                            onChange={handleEditChange}
                                        />
                                        <label className="form-check-label" htmlFor="editIsEnabled">Enable User</label>
                                    </div>
                                    <button type="submit" className="btn btn-primary">Save Changes</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDeleteModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Are you sure you want to delete user <strong>{userToDelete}</strong>?</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDelete}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Visit Timestamps Modal */}
            {showTimestampsModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Visit Timestamps for {selectedUser.username}</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowTimestampsModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {selectedUser.visit_timestamps && selectedUser.visit_timestamps.length > 0 ? (
                                    <ul className="list-group">
                                        {selectedUser.visit_timestamps.slice().reverse().map((ts, index) => (
                                            <li key={index} className="list-group-item">
                                                {new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false }) + ' IST'}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>No visit timestamps available.</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowTimestampsModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;